import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { API_URL } from "../config";
import { decrementPending, incrementPending } from "../Store/loaderSlice";
import {
  IApiCustomerResponse,
  IApiLoopingResponse,
  IApiResponse,
  IApiResponseInvoice,
  IApiResponseWorkOrder,
  ICategoryFilterPayload,
  ICategoryPayload,
  ICustomerProjectPayload,
  ICustomerTransactionPayload,
  IGetByCategoryArrayPayload,
  IGetByCategoryPayload,
  IInvoiceListPayload,
  ILoopingTableListPayload,
  IPriceHistoryPayload,
  IProductPayload,
  IRemovedorkOrderByCustomerProjectPayload,
  ISearchIsActivePayload,
  ISearchPayload,
  ISelectCustomerPayload,
  IUpdateStatuInvoicePayload,
  IUpdateWorkOrderByCustomerProjectPayload,
  IUser,
  IWorkOrderByCustomerProjectPayload,
  IWorkOrderHistoryPayload,
  IAssignDriverPayload,
  IInvoiceHistoryPayload,
  IEmail,
  IText,
  INote,
  INoteEmail,
  IActivityHistoryApiResponse,
  IActivityHistoryProjectApiResponse,
  IApInvoicePayload,
  IApInvoiceApiResponse,
  IApSendPayload,
  IApHistoryPayload,
  IApApiResponse,
  IApCustomersByDatePayload,
  IContractInvoicePayload,
  IContractInvoicePaymentPayload,
  IContract,
  IDriverPaymentPayload,
  IDriverPaymentListPayload,
  ILead,
  ILeadListPayload,
  ILeadCommunicationPayload,
  IPayrollFilterPayload,
  IRecordPaymentPayload,
  IStoreExpensePayload,
  IBalanceSheetFilter,
  ITripProfitFilter,
  IRoutePriceSuggestionPayload,
  ITripSmsLogsPayload,
  ITripSmsLogsResponse,
  ISmsTemplate,
  IAssignLegDriverPayload,
  IAvailableDriversPayload,
  ICreateRolePayload,
  ICreateUserPayload,
  IUpdateUserPayload,
  IOnlinePaymentListPayload,
  IOnlinePaymentListResponse,
  IBusinessProfilePayload,
} from "./Interface/api.interface";
/**
 * Holds all the API callbacks
 * @returns RTK Implementation for backend
 */

const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_URL,
  prepareHeaders: (headers) => {
    headers.set("Content-Type", "application/json");
    headers.set(
      "Authorization",
      `Bearer ${localStorage.getItem("token") || ""}`
    );
    return headers;
  },
});

// Endpoints that run in the background (e.g. polling) and must NEVER show
// the global loader — otherwise the loader flashes every poll interval.
const SILENT_ENDPOINTS = new Set<string>([
  "getNotifications",
]);

const baseQueryWithLoader: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const isSilent = SILENT_ENDPOINTS.has((api as any)?.endpoint || "");
  if (!isSilent) api.dispatch(incrementPending());
  try {
    return await rawBaseQuery(args, api, extraOptions);
  } finally {
    if (!isSilent) api.dispatch(decrementPending());
  }
};

export const attoDeskApi = createApi({
  reducerPath: "attoDeskApi",
  baseQuery: baseQueryWithLoader,
  tagTypes: [
    "User",
    "category",
    "company",
    "department",
    "coursing",
    "tax",
    "url",
    "product",
    "printer",
    "productBrand",
    "productTag",
    "productCategory",
    "modifier",
    "productGetByCategory",
    "looping",
    "customer",
    "quote",
    "workOrderHistroy",
    "assignDriverHistory",
    "invoiceHistroy",
    "workOrderHistroyCustomer",
    "invoiceHistroyCustomer",
    "invoice",
    "driver",
    "email",
    "note",
    "noteEmail",
    "emailTemplate",
    "noteTemplate",
    "activityHistory",
    "activityHistoryProject",
    "text",
    "apEmail",
    "contractInvoice",
    "tripSmsLog",
    "smsTemplate",
    "driverPayment",
    "lead",
    "businessProfile",
    "payroll",
    "tripProfit",
    "tripLeg",
    "notification",
    "userList",
    "role",
    "privilege",
    "onlinePayment",
    "quickPaymentLink",
  ],
  endpoints: (builder) => ({
    getUser: builder.query<IApiResponse, void>({
      query: (request) => {
        return {
          url: "/user/userInfo",
          method: "POST",
          body: request,
        };
      },
      keepUnusedDataFor: 300,
      providesTags: ["User"],
    }),
    login: builder.mutation<IApiResponse, IUser>({
      query: (request) => {
        return {
          url: "/login",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
    }),
    forgotPassword: builder.mutation<IApiResponse, { email: string }>({
      query: (body) => ({
        url: "/forgot-password",
        method: "POST",
        body: JSON.stringify(body),
      }),
    }),
    resetPassword: builder.mutation<IApiResponse, {
      email: string;
      token: string;
      password: string;
      password_confirmation: string;
    }>({
      query: (body) => ({
        url: "/reset-password",
        method: "POST",
        body: JSON.stringify(body),
      }),
    }),
    createCategory: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/category",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["category", "productCategory"],
    }),
    createCompany: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/company",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["company"],
    }),
    createDepartment: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/department",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["department"],
    }),
    createCoursing: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/coursing",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["coursing"],
    }),
    createTax: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/tax",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["tax", "product", "category"],
    }),
    createUrl: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/url",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["url"],
    }),
    createProductBrand: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/product/brand",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["productBrand", "product"],
    }),
    createProductTag: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/product/tags",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["productTag", "product"],
    }),
    createProductCategory: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/product/category",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["productCategory", "product"],
    }),
    createProduct: builder.mutation<IApiLoopingResponse, Object>({
      query: (request) => {
        return {
          url: "/product",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["product"],
    }),
    createPrinter: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/printer",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["printer", "product"],
    }),
    getCategory: builder.query<IApiResponse, void>({
      query: () => {
        return {
          url: "/category/getAll",
          method: "POST",
        };
      },
      providesTags: ["product", "category"],
      keepUnusedDataFor: 0,
    }),
    getAllMainCategory: builder.query<IApiResponse, ICategoryPayload>({
      query: (request) => {
        return {
          url: "/main-category/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["product", "category"],
      keepUnusedDataFor: 0,
    }),
    deleteCoursing: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/coursing/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["coursing"],
    }),
    deleteDepartment: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/department/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["department"],
    }),
    deletePrinter: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/printer/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["printer"],
    }),
    deleteEmailTemplate: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/email-template/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["emailTemplate"],
    }),
    deleteNoteTemplate: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/note-template/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["noteTemplate"],
    }),
    deleteProductBrand: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/product/brand/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["productBrand"],
    }),
    deleteProductTag: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/product/tags/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["productTag"],
    }),
    deleteTax: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/tax/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["tax"],
    }),
    deleteUrl: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/url/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["url"],
    }),
    deleteProductCategory: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/product/category/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["productCategory"],
    }),
    deleteCategory: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/category/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["category", "productCategory"],
    }),
    getProduct: builder.query<IApiResponse, IProductPayload>({
      query: (request) => {
        return {
          url: "/product/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["productTag", "product", "quote"],
      keepUnusedDataFor: 0,
    }),
    createGetAllProduct: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/product/getAll",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["productTag", "product"],
    }),
    deleteProduct: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/product/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["product"],
    }),
    getLastProductId: builder.query<IApiResponse, void>({
      query: () => {
        return {
          url: "/product/last-id",
          method: "GET",
        };
      },
      providesTags: ["product"],
      keepUnusedDataFor: 0,
    }),
    createModifier: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/modifier",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["modifier"],
    }),
    getAllModifier: builder.query<IApiResponse, ISearchPayload>({
      query: (request) => {
        return {
          url: "/modifier/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["modifier"],
      keepUnusedDataFor: 0,
    }),
    deleteModifier: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/modifier/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["modifier"],
    }),
    getDepartment: builder.query<IApiResponse, ISearchPayload>({
      query: (request) => {
        return {
          url: "/department/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["department", "category"],
      keepUnusedDataFor: 0,
    }),
    getTax: builder.query<IApiResponse, ISearchPayload>({
      query: (request) => {
        return {
          url: "/tax/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["tax", "category", "product"],
      keepUnusedDataFor: 0,
    }),
    getUrl: builder.query<IApiResponse, ISearchPayload>({
      query: (request) => {
        return {
          url: "/url/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["url"],
      keepUnusedDataFor: 0,
    }),
    getCoursing: builder.query<IApiResponse, ISearchPayload>({
      query: (request) => {
        return {
          url: "/coursing/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["coursing", "category"],
      keepUnusedDataFor: 0,
    }),
    getPrinter: builder.query<IApiResponse, ISearchPayload>({
      query: (request) => {
        return {
          url: "/printer/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["printer", "product"],
      keepUnusedDataFor: 0,
    }),
    getEmailTemplate: builder.query<IApiResponse, ISearchPayload>({
      query: (request) => {
        return {
          url: "/email-template/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["emailTemplate"],
      keepUnusedDataFor: 0,
    }),
    getEmailTemplateByName: builder.query<IApiResponse, string>({
      query: (name) => {
        return {
          url: "/email-template/getByName",
          method: "POST",
          body: { name },
        };
      },
      keepUnusedDataFor: 0,
    }),
    getWorkOrderNotes: builder.query<IApiResponse, string>({
      query: (workOrderId) => ({
        url: `/work-order-notes/${workOrderId}`,
        method: "GET",
      }),
      providesTags: ["note"],
      keepUnusedDataFor: 0,
    }),
    getNoteTemplate: builder.query<IApiResponse, ISearchPayload>({
      query: (request) => {
        return {
          url: "/note-template/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["noteTemplate"],
      keepUnusedDataFor: 0,
    }),
    getProductTag: builder.query<IApiResponse, ISearchIsActivePayload>({
      query: (request) => {
        return {
          url: "/product/tags/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["productTag", "product"],
      keepUnusedDataFor: 0,
    }),
    getProductBrand: builder.query<IApiResponse, ISearchIsActivePayload>({
      query: (request) => {
        return {
          url: "/product/brand/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["productBrand", "product"],
      keepUnusedDataFor: 0,
    }),
    getProductCategory: builder.query<IApiResponse, ISearchIsActivePayload>({
      query: (request) => {
        return {
          url: "/product/category/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["productCategory", "product"],
      keepUnusedDataFor: 0,
    }),
    // getSubCategoryByCategoryId: builder.query<IApiResponse, ISearchIsActivePayload>({
    //   query: (request) => {
    //     return {
    //       url: '/product/category/getAll',
    //       method: "POST",
    //       body: request,
    //     };
    //   },
    //   providesTags:['productCategory','product'],
    //   keepUnusedDataFor: 0,
    // }),
    getSubCategoryByCategoryId: builder.query<
      IApiResponse,
      IGetByCategoryPayload
    >({
      query: (request) => {
        return {
          url: "/product/get-by-categories",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["productGetByCategory", "product"],
      keepUnusedDataFor: 0,
    }),
    getSubCategoryByArray: builder.query<
      IApiResponse,
      IGetByCategoryArrayPayload
    >({
      query: (request) => {
        return {
          url: "/category/category-fiter/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["productGetByCategory", "product", "productCategory"],
      keepUnusedDataFor: 0,
    }),
    getProductSubCategory: builder.query<IApiResponse, ISearchIsActivePayload>({
      query: (request) => {
        return {
          url: "/category/sub-category/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["productCategory", "product"],
      keepUnusedDataFor: 0,
    }),
    getAllCategoryName: builder.query<IApiResponse, void>({
      query: () => {
        return {
          url: "/all-category/getAll",
          method: "POST",
        };
      },
      providesTags: ["productCategory"],
      keepUnusedDataFor: 0,
    }),
    getCtegoryFilter: builder.query<IApiResponse, ICategoryFilterPayload>({
      query: (request) => {
        return {
          url: "/category/filter",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["product"],
      keepUnusedDataFor: 0,
    }),
    getLoopingTable: builder.query<IApiResponse, ISearchPayload>({
      query: (request) => {
        return {
          url: "/looping/list-tables",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["category", "product", "looping"],
      keepUnusedDataFor: 0,
    }),
    createLoopingTable: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/looping/create-table",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["looping", "product"],
    }),
    getAllLoopingTable: builder.query<IApiResponse, ILoopingTableListPayload>({
      query: (request) => {
        return {
          url: "/looping/list-tables",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["looping", "product"],
      keepUnusedDataFor: 0,
    }),
    deleteLoopingTable: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/looping/delete-table",
          method: "DELETE",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["looping", "product"],
    }),
    updateLoopingTable: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/looping/update-table",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["looping", "product"],
    }),
    insertOrUpdateDataLoopingTable: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/looping/table/insert-update-data",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["product", "looping"],
    }),
    getCustomer: builder.query<IApiResponse, ILoopingTableListPayload>({
      query: (request) => {
        return {
          url: "/customer/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["customer"],
      keepUnusedDataFor: 0,
    }),
    getActivityHistory: builder.query<IActivityHistoryApiResponse, ILoopingTableListPayload>({
      query: (request) => {
        return {
          url: "/customer/activityHistory/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["activityHistory"],
      keepUnusedDataFor: 0,
    }),

    getProjectActivityHistory: builder.query<IActivityHistoryProjectApiResponse, ICustomerProjectPayload>({
      query: (request) => {
        return {
          url: "/customer/project/activityHistory/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["activityHistoryProject"],
      keepUnusedDataFor: 0,
    }),
    deleteCustomer: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/customer/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["customer"],
    }),
    deleteWorkOrder: builder.mutation<IApiResponse, string>({
      query: (id) => ({
        url: `/work-order/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["quote"],
    }),
    createCustomer: builder.mutation<IApiCustomerResponse, Object>({
      query: (request) => {
        return {
          url: "/customer",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["customer"],
    }),
    updateCustomer: builder.mutation<IApiCustomerResponse, { id: number | string; [key: string]: any }>({
      query: (request) => ({
        url: `/customer`,
        method: "POST",
        body: JSON.stringify(request),
      }),
      invalidatesTags: ["customer"],
    }),
    getCustomerTransaction: builder.query<
      IApiResponse,
      ICustomerTransactionPayload
    >({
      query: (request) => {
        return {
          url: "/customer/transaction/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["customer"],
      keepUnusedDataFor: 0,
    }),
    deleteCustomerTransaction: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/customer/transaction/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["customer"],
    }),
    createCustomerTransaction: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/customer/transaction",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["customer"],
    }),
    getCompany: builder.query<IApiResponse, ILoopingTableListPayload>({
      query: (request) => {
        return {
          url: "/company/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["company"],
      keepUnusedDataFor: 0,
    }),
    deleteCompany: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/company/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["company"],
    }),
    getAllSearchCompany: builder.query<IApiResponse, ILoopingTableListPayload>({
      query: (request) => {
        return {
          url: "/company/search/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["company"],
      keepUnusedDataFor: 0,
    }),
    createWorkOrder: builder.mutation<IApiResponseWorkOrder, Object>({
      query: (request) => {
        return {
          url: "/work-order",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["quote"],
    }),
    editInvoice: builder.mutation<IApiResponseWorkOrder, Object>({
      query: (request) => {
        return {
          url: "/work-order/edit-invoice",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["quote", "invoice", "workOrderHistroy", "invoiceHistroy"],
    }),
    getLastWorkOrderId: builder.query<IApiResponse, void>({
      query: () => {
        return {
          url: "/work-order/next-id",
          method: "GET",
        };
      },
      providesTags: ["quote"],
      keepUnusedDataFor: 0,
    }),
    getWorkOrder: builder.query<IApiResponse, ILoopingTableListPayload>({
      query: (request) => {
        return {
          url: "/work-order/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["quote", "workOrderHistroy"],
      keepUnusedDataFor: 0,
    }),
    updateInvoiceStatus: builder.query<
      IApiResponse,
      IUpdateStatuInvoicePayload
    >({
      query: (request) => {
        return {
          url: "/work-order/update-invoice",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["workOrderHistroy"],
      keepUnusedDataFor: 0,
    }),
    convertToInvoice: builder.mutation<IApiResponse, IUpdateStatuInvoicePayload>({
      query: (request) => ({
        url: "/work-order/update-invoice",
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["workOrderHistroy", "invoice"],
    }),
    getAllInvoice: builder.query<IApiResponse, IInvoiceListPayload>({
      query: (request) => {
        return {
          url: "/invoice/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["invoice"],
      keepUnusedDataFor: 0,
    }),
    getAllWorkOrderHistory: builder.query<
      IApiResponse,
      IWorkOrderHistoryPayload
    >({
      query: (request) => {
        return {
          url: "/work-order-history",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["workOrderHistroy"],
      keepUnusedDataFor: 0,
    }),

    getAllAssignDriverHistory: builder.query<
      IApiResponse,
      IAssignDriverPayload
    >({
      query: (request) => {
        return {
          url: "/assign-driver-history",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["assignDriverHistory"],
      keepUnusedDataFor: 0,
    }),
    getAllInvoiceHistory: builder.query<
      IApiResponse,
      IInvoiceHistoryPayload
    >({
      query: (request) => {
        return {
          url: "/invoice-history",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["invoiceHistroy"],
      keepUnusedDataFor: 0,
    }),
    getAllWorkOrderHistoryCustomer: builder.query<
      IApiResponse,
      IWorkOrderHistoryPayload
    >({
      query: (request) => {
        return {
          url: "/work-order-history-by-customer",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["workOrderHistroyCustomer"],
      keepUnusedDataFor: 0,
    }),
    createDriver: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/driver",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["driver"],
    }),
    getDriver: builder.query<IApiResponse, ILoopingTableListPayload>({
      query: (request) => {
        return {
          url: "/driver/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["driver"],
      keepUnusedDataFor: 0,
    }),
    deleteDriver: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/driver/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["driver"],
    }),
    getPriceHistory: builder.query<IApiResponse, IPriceHistoryPayload>({
      query: (request) => {
        return {
          url: "/work-order/price",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["quote"],
      keepUnusedDataFor: 0,
    }),
    getRoutePriceSuggestion: builder.query<IApiResponse, IRoutePriceSuggestionPayload>({
      query: (request) => {
        return {
          url: "/work-order/price-by-route",
          method: "POST",
          body: request,
        };
      },
      keepUnusedDataFor: 300, // cache per route-key for 5 minutes
    }),
    getCustomerProject: builder.query<IApiResponse,  ICustomerProjectPayload >({
      query: (request) => {
        return {
          url: "/customer/project/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["customer"],
      keepUnusedDataFor: 0,
    }),
    deleteCustomerProject: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/customer/project/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["customer"],
    }),
    createCustomerProject: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/customer/project",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["customer"],
    }),
    getWorkOrderByCustomerProject: builder.query<
      IApiResponse,
      IWorkOrderByCustomerProjectPayload
    >({
      query: (request) => {
        return {
          url: "/work-order/project/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["customer"],
      keepUnusedDataFor: 0,
    }),
    getSelectorWorkOrderByCustomerProject: builder.query<IApiResponse, ISelectCustomerPayload>({
      query: (request) => {
        return {
          url: "/work-order/project/selector/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["customer"],
      keepUnusedDataFor: 0,
    }),

    updateWorkOrderByCustomerProject: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/work-order/assign-project",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["customer"],
    }),
    removedCustomerProjectM: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/work-order/project/remove",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["customer"],
    }),
    createWorkOrderWithProject: builder.mutation<IApiResponseWorkOrder, Object>({
      query: (request) => {
        return {
          url: "/work-order/project-id",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["quote"],
    }),
    createEmail: builder.mutation<IEmail, Object>({
      query: (request) => {
        return {
          url: "/send-email",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["email"],
    }),

    sendQuoteEmail: builder.mutation<{ code: number; status: boolean; message: string; pdf: string | null }, { work_order_id: number; to?: string }>({
      query: (request) => ({
        url: "/send-quote-email",
        method: "POST",
        body: JSON.stringify(request),
      }),
      invalidatesTags: ["email"],
    }),

    createEmailInvoice: builder.mutation<IApiResponseInvoice, FormData>({
      query: (formData) => {
        return {
          url: "/send-invoice-email",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["email"],
    }),

    createText: builder.mutation<IText, Object>({
      query: (request) => {
        return {
          url: "/send-text",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["text"],
    }),
    
    
  
  createNote: builder.mutation<INote, Object>({
    query: (request) => {
      return {
        url: "/save-note",
        method: "POST",
        body: JSON.stringify(request),
      };
    },
    invalidatesTags: ["note"],
  }),

  createNoteEmail: builder.mutation<INoteEmail, Object>({
    query: (request) => {
      return {
        url: "/send-note-email",
        method: "POST",
        body: JSON.stringify(request),
      };
    },
    invalidatesTags: ["noteEmail"],
  }),

  createEmailTemplate: builder.mutation<IApiResponse, Object>({
    query: (request) => {
      return {
        url: "/email-template",
        method: "POST",
        body: JSON.stringify(request),
      };
    },
    invalidatesTags: ["emailTemplate"],
  }),
  createNoteTemplate: builder.mutation<IApiResponse, Object>({
    query: (request) => {
      return {
        url: "/note-template",
        method: "POST",
        body: JSON.stringify(request),
      };
    },
    invalidatesTags: ["noteTemplate"],
  }),

  // ── AP Email endpoints ─────────────────────────────────────────────────────
  getApInvoices: builder.mutation<IApInvoiceApiResponse, IApInvoicePayload>({
    query: (request) => ({
      url: "/ap/invoices",
      method: "POST",
      body: JSON.stringify(request),
    }),
  }),

  sendApEmail: builder.mutation<IApApiResponse, IApSendPayload>({
    query: (request) => ({
      url: "/ap/send",
      method: "POST",
      body: JSON.stringify(request),
    }),
    invalidatesTags: ["apEmail"],
  }),

  getApHistory: builder.mutation<IApApiResponse, IApHistoryPayload>({
    query: (request) => ({
      url: "/ap/history",
      method: "POST",
      body: JSON.stringify(request),
    }),
  }),

  getAllApHistory: builder.query<IApApiResponse, void>({
    query: () => ({
      url: "/ap/history/all",
      method: "POST",
      body: JSON.stringify({}),
    }),
    providesTags: ["apEmail"],
    keepUnusedDataFor: 0,
  }),

  getApCustomersByDate: builder.mutation<IApiResponse, IApCustomersByDatePayload>({
    query: (request) => ({
      url: "/ap/customers-by-date",
      method: "POST",
      body: JSON.stringify(request),
    }),
  }),

  // ── Contract Invoice endpoints ────────────────────────────────────────────
  createContractInvoice: builder.mutation<IApiResponse, Object>({
    query: (request) => ({
      url: "/contract-invoice",
      method: "POST",
      body: JSON.stringify(request),
    }),
    invalidatesTags: ["contractInvoice"],
  }),

  getContractInvoices: builder.query<IApiResponse, IContractInvoicePayload>({
    query: (request) => ({
      url: "/contract-invoice/getAll",
      method: "POST",
      body: request,
    }),
    providesTags: ["contractInvoice"],
    keepUnusedDataFor: 0,
  }),

  getNextContractInvoiceId: builder.query<IApiResponse, void>({
    query: () => ({
      url: "/contract-invoice/next-id",
      method: "GET",
    }),
    providesTags: ["contractInvoice"],
    keepUnusedDataFor: 0,
  }),

  getContractInvoiceSuggestByDate: builder.query<IApiResponse, string>({
    query: (date) => ({
      url: `/contract-invoice/suggest-by-date?date=${date}`,
      method: "GET",
    }),
    keepUnusedDataFor: 0,
  }),

  // ── Recurring contracts ──────────────────────────────────────────────────
  createContract: builder.mutation<IApiResponse, Object>({
    query: (request) => ({
      url: "/contract",
      method: "POST",
      body: JSON.stringify(request),
    }),
    invalidatesTags: ["contractInvoice"],
  }),

  getContracts: builder.query<IApiResponse, { searchText?: string; client_id?: string; id?: string }>({
    query: (request) => ({
      url: "/contract/getAll",
      method: "POST",
      body: request,
    }),
    keepUnusedDataFor: 0,
  }),

  getContractLookup: builder.query<IApiResponse, { clientId: string; date: string }>({
    query: ({ clientId, date }) => ({
      url: `/contract/lookup?client_id=${clientId}&date=${date}`,
      method: "GET",
    }),
    keepUnusedDataFor: 0,
  }),

  getContractById: builder.query<IApiResponse, string>({
    query: (id) => ({
      url: `/contract/${id}`,
      method: "GET",
    }),
    keepUnusedDataFor: 0,
  }),

  deleteContract: builder.mutation<IApiResponse, string>({
    query: (id) => ({
      url: `/contract/${id}`,
      method: "DELETE",
    }),
    invalidatesTags: ["contractInvoice"],
  }),

  deleteContractInvoice: builder.mutation<IApiResponse, string>({
    query: (id) => ({
      url: `/contract-invoice/${id}`,
      method: "DELETE",
    }),
    invalidatesTags: ["contractInvoice"],
  }),

  updateContractInvoicePayment: builder.mutation<IApiResponse, IContractInvoicePaymentPayload>({
    query: (request) => ({
      url: "/contract-invoice/payment",
      method: "POST",
      body: JSON.stringify(request),
    }),
    invalidatesTags: ["contractInvoice"],
  }),

  // ── Trip SMS ────────────────────────────────────────────────────────────────
  getTripSmsLogs: builder.query<ITripSmsLogsResponse, ITripSmsLogsPayload>({
    query: (request) => ({
      url: "/trip-sms/logs",
      method: "POST",
      body: JSON.stringify(request),
    }),
    providesTags: ["tripSmsLog"],
    keepUnusedDataFor: 0,
  }),

  resendTripSms: builder.mutation<IApiResponse, ITripSmsLogsPayload>({
    query: (request) => ({
      url: "/trip-sms/resend",
      method: "POST",
      body: JSON.stringify(request),
    }),
    invalidatesTags: ["tripSmsLog"],
  }),

  // ── SMS Templates ────────────────────────────────────────────────────────────
  getSmsTemplates: builder.query<IApiResponse, { searchText?: string }>({
    query: (request) => ({
      url: "/sms-template/getAll",
      method: "POST",
      body: request,
    }),
    providesTags: ["smsTemplate"],
    keepUnusedDataFor: 0,
  }),
  createSmsTemplate: builder.mutation<IApiResponse, Partial<ISmsTemplate> & { id?: number }>({
    query: (request) => ({
      url: "/sms-template",
      method: "POST",
      body: JSON.stringify(request),
    }),
    invalidatesTags: ["smsTemplate"],
  }),
  deleteSmsTemplate: builder.mutation<IApiResponse, string>({
    query: (id) => ({
      url: `/sms-template/${id}`,
      method: "DELETE",
    }),
    invalidatesTags: ["smsTemplate"],
  }),
  toggleSmsTemplate: builder.mutation<IApiResponse, { id: number }>({
    query: (request) => ({
      url: "/sms-template/toggle",
      method: "POST",
      body: JSON.stringify(request),
    }),
    invalidatesTags: ["smsTemplate"],
  }),
  duplicateSmsTemplate: builder.mutation<IApiResponse, { id: number }>({
    query: (request) => ({
      url: "/sms-template/duplicate",
      method: "POST",
      body: JSON.stringify(request),
    }),
    invalidatesTags: ["smsTemplate"],
  }),

  // ── Driver Payments ────────────────────────────────────────────────────────
  createDriverPayment: builder.mutation<IApiResponse, IDriverPaymentPayload>({
    query: (request) => ({
      url: "/driver-payment",
      method: "POST",
      body: JSON.stringify(request),
    }),
    invalidatesTags: ["driverPayment"],
  }),
  getDriverPayments: builder.query<IApiResponse, IDriverPaymentListPayload>({
    query: (request) => ({
      url: "/driver-payment/getAll",
      method: "POST",
      body: request,
    }),
    providesTags: ["driverPayment"],
    keepUnusedDataFor: 0,
  }),
  markDriverPaymentPaid: builder.mutation<IApiResponse, { id: number }>({
    query: (request) => ({
      url: "/driver-payment/mark-paid",
      method: "POST",
      body: JSON.stringify(request),
    }),
    invalidatesTags: ["driverPayment"],
  }),

  // ── Business Profile (Settings → Business Profile) ──────────────────────────
  // Portal/login/email branding and website-lead settings. Separate from the
  // invoice Company records under Settings → Company.
  getBusinessProfile: builder.query<IApiResponse, void>({
    query: () => ({ url: "/business-profile", method: "GET" }),
    providesTags: ["businessProfile"],
  }),
  updateBusinessProfile: builder.mutation<IApiResponse, IBusinessProfilePayload>({
    query: (body) => ({
      url: "/business-profile",
      method: "PUT",
      body: JSON.stringify(body),
    }),
    invalidatesTags: ["businessProfile"],
  }),
  // Public: the login page needs the logo before anyone has signed in.
  getPublicBranding: builder.query<IApiResponse, void>({
    query: () => ({ url: "/branding", method: "GET" }),
    providesTags: ["businessProfile"],
  }),

  // ── Lead / Enquiry endpoints ────────────────────────────────────────────────
  getLeads: builder.query<IApiResponse, ILeadListPayload>({
    query: (params) => ({
      url: "/leads",
      method: "GET",
      headers: { "x-api-key": process.env.REACT_APP_LEAD_API_KEY || "" },
      params,
    }),
    providesTags: ["lead"],
    keepUnusedDataFor: 0,
  }),
  getLead: builder.query<IApiResponse, number>({
    query: (id) => ({
      url: `/leads/${id}`,
      method: "GET",
      headers: { "x-api-key": process.env.REACT_APP_LEAD_API_KEY || "" },
    }),
    providesTags: ["lead"],
  }),
  updateLeadStatus: builder.mutation<IApiResponse, { id: number; status: string }>({
    query: ({ id, status }) => ({
      url: `/leads/${id}/status`,
      method: "PUT",
      headers: { "x-api-key": process.env.REACT_APP_LEAD_API_KEY || "" },
      body: JSON.stringify({ status }),
    }),
    invalidatesTags: ["lead"],
  }),
  addLeadCommunication: builder.mutation<IApiResponse, { id: number } & ILeadCommunicationPayload>({
    query: ({ id, ...body }) => ({
      url: `/leads/${id}/communication`,
      method: "POST",
      headers: { "x-api-key": process.env.REACT_APP_LEAD_API_KEY || "" },
      body: JSON.stringify(body),
    }),
    invalidatesTags: ["lead"],
  }),
  convertLead: builder.mutation<IApiResponse, number>({
    query: (id) => ({
      url: `/leads/${id}/convert`,
      method: "POST",
      headers: { "x-api-key": process.env.REACT_APP_LEAD_API_KEY || "" },
    }),
    invalidatesTags: ["lead"],
  }),
  createLead: builder.mutation<IApiResponse, object>({
    query: (body) => ({
      url: "/leads",
      method: "POST",
      headers: { "x-api-key": process.env.REACT_APP_LEAD_API_KEY || "" },
      body: JSON.stringify(body),
    }),
    invalidatesTags: ["lead"],
  }),
  deleteLead: builder.mutation<IApiResponse, number>({
    query: (id) => ({
      url: `/leads/${id}`,
      method: "DELETE",
      headers: { "x-api-key": process.env.REACT_APP_LEAD_API_KEY || "" },
    }),
    invalidatesTags: ["lead"],
  }),

  // ── Previous trips by customer ─────────────────────────────────────────────
  getTripsByCustomer: builder.query<IApiResponse, string>({
    query: (customerId) => ({
      url: `/work-order/trips-by-customer/${customerId}`,
      method: "GET",
    }),
    providesTags: ["quote"],
    keepUnusedDataFor: 0,
  }),

  // ── Driver Payroll endpoints ────────────────────────────────────────────────
  getPayrollSummary: builder.query<IApiResponse, IPayrollFilterPayload>({
    query: (params) => ({ url: "/driver-payroll/summary", method: "GET", params }),
    providesTags: ["payroll"],
    keepUnusedDataFor: 0,
  }),
  getPayrollIncome: builder.query<IApiResponse, IPayrollFilterPayload>({
    query: (params) => ({ url: "/driver-payroll/income", method: "GET", params }),
    providesTags: ["payroll"],
    keepUnusedDataFor: 0,
  }),
  recordPayment: builder.mutation<IApiResponse, IRecordPaymentPayload>({
    query: ({ id, ...body }) => ({
      url: `/driver-payroll/income/${id}/payment`,
      method: "PUT",
      body: JSON.stringify(body),
    }),
    invalidatesTags: ["payroll", "driverPayment"],
  }),
  getPayrollExpenses: builder.query<IApiResponse, IPayrollFilterPayload>({
    query: (params) => ({ url: "/driver-payroll/expenses", method: "GET", params }),
    providesTags: ["payroll"],
    keepUnusedDataFor: 0,
  }),
  storePayrollExpense: builder.mutation<IApiResponse, IStoreExpensePayload>({
    query: (body) => ({
      url: "/driver-payroll/expenses",
      method: "POST",
      body: JSON.stringify(body),
    }),
    invalidatesTags: ["payroll"],
  }),
  deletePayrollExpense: builder.mutation<IApiResponse, number>({
    query: (id) => ({ url: `/driver-payroll/expenses/${id}`, method: "DELETE" }),
    invalidatesTags: ["payroll"],
  }),
  getPayrollTransactions: builder.query<IApiResponse, IPayrollFilterPayload>({
    query: (params) => ({ url: "/driver-payroll/transactions", method: "GET", params }),
    providesTags: ["payroll"],
    keepUnusedDataFor: 0,
  }),

  // ── Balance Sheet ────────────────────────────────────────────────────────────
  getBalanceSheetAdvanced: builder.query<IApiResponse, IBalanceSheetFilter>({
    query: (params) => ({ url: "/balance-sheet/advanced", method: "GET", params }),
    keepUnusedDataFor: 0,
  }),
  getBalanceSheetSummary: builder.query<IApiResponse, IBalanceSheetFilter>({
    query: (params) => ({ url: "/balance-sheet/summary", method: "GET", params }),
    keepUnusedDataFor: 0,
  }),
  getBalanceSheetIncome: builder.query<IApiResponse, IBalanceSheetFilter>({
    query: (params) => ({ url: "/balance-sheet/income", method: "GET", params }),
    keepUnusedDataFor: 0,
  }),
  getBalanceSheetExpenses: builder.query<IApiResponse, IBalanceSheetFilter>({
    query: (params) => ({ url: "/balance-sheet/expenses", method: "GET", params }),
    keepUnusedDataFor: 0,
  }),
  getBalanceSheetTransactions: builder.query<IApiResponse, IBalanceSheetFilter>({
    query: (params) => ({ url: "/balance-sheet/transactions", method: "GET", params }),
    keepUnusedDataFor: 0,
  }),

  // ── Trip Profit ──────────────────────────────────────────────────────────────
  getTripProfit: builder.query<IApiResponse, ITripProfitFilter>({
    query: (params) => ({ url: "/trips/profit", method: "GET", params }),
    providesTags: ["tripProfit"],
    keepUnusedDataFor: 0,
  }),
  getTripProfitById: builder.query<IApiResponse, number>({
    query: (id) => ({ url: `/trips/profit/${id}`, method: "GET" }),
    providesTags: ["tripProfit"],
    keepUnusedDataFor: 0,
  }),
  getTripProfitMonthly: builder.query<IApiResponse, ITripProfitFilter>({
    query: (params) => ({ url: "/trips/profit/monthly", method: "GET", params }),
    providesTags: ["tripProfit"],
    keepUnusedDataFor: 0,
  }),

  // ── Trip Legs ─────────────────────────────────────────────────────────────
  getTripLegs: builder.query<IApiResponse, { date?: string; work_order_id?: string }>({
    query: (params) => ({ url: "/legs", method: "GET", params }),
    providesTags: ["tripLeg"],
    keepUnusedDataFor: 0,
  }),
  splitTripLegs: builder.mutation<IApiResponse, string | number>({
    query: (workOrderId) => ({
      url: `/bookings/${workOrderId}/split-legs`,
      method: "POST",
    }),
    invalidatesTags: ["tripLeg"],
  }),
  assignLegDriver: builder.mutation<IApiResponse, IAssignLegDriverPayload>({
    query: ({ leg_id, driver_id, force }) => ({
      url: `/legs/${leg_id}/assign-driver`,
      method: "PATCH",
      body: { driver_id, force },
    }),
    invalidatesTags: ["tripLeg"],
  }),
  getAvailableDrivers: builder.query<IApiResponse, IAvailableDriversPayload>({
    query: (params) => ({ url: "/drivers/available", method: "GET", params }),
    keepUnusedDataFor: 0,
  }),
  sendTripDriverSms: builder.mutation<IApiResponse, { workOrderId: string; phone: string; message: string }>({
    query: ({ workOrderId, phone, message }) => ({
      url: `/trips/${workOrderId}/send-sms`,
      method: "POST",
      body: { phone, message },
    }),
    invalidatesTags: ["tripSmsLog"],
  }),

  // ── Notifications ────────────────────────────────────────────────────────────
  getNotifications: builder.query<IApiResponse, void>({
    query: () => ({ url: "/notifications", method: "GET" }),
    providesTags: ["notification"],
    keepUnusedDataFor: 30,
  }),
  markNotificationRead: builder.mutation<IApiResponse, string>({
    query: (id) => ({ url: `/notifications/${id}/read`, method: "POST" }),
    invalidatesTags: ["notification"],
  }),
  markAllNotificationsRead: builder.mutation<IApiResponse, void>({
    query: () => ({ url: "/notifications/read-all", method: "POST" }),
    invalidatesTags: ["notification"],
  }),

  // ── User Management ──────────────────────────────────────────────────────────
  getUsersList: builder.query<IApiResponse, { search?: string }>({
    query: ({ search = "" }) => ({ url: `/users?search=${search}`, method: "GET" }),
    keepUnusedDataFor: 30,
    providesTags: ["userList"],
  }),
  createUserAccount: builder.mutation<IApiResponse, ICreateUserPayload>({
    query: (body) => ({ url: "/users", method: "POST", body: JSON.stringify(body) }),
    invalidatesTags: ["userList"],
  }),
  updateUserAccount: builder.mutation<IApiResponse, { id: number } & IUpdateUserPayload>({
    query: ({ id, ...body }) => ({ url: `/users/${id}`, method: "PUT", body: JSON.stringify(body) }),
    invalidatesTags: ["userList"],
  }),
  deleteUserAccount: builder.mutation<IApiResponse, number>({
    query: (id) => ({ url: `/users/${id}`, method: "DELETE" }),
    invalidatesTags: ["userList"],
  }),
  generateUserPassword: builder.mutation<IApiResponse & { password?: string }, void>({
    query: () => ({ url: "/users/generate-password", method: "POST" }),
  }),
  sendUserCredentials: builder.mutation<IApiResponse, { id: number; password: string }>({
    query: ({ id, password }) => ({
      url: `/users/${id}/send-credentials`,
      method: "POST",
      body: JSON.stringify({ password }),
    }),
  }),
  resetUserCredentials: builder.mutation<IApiResponse, { id: number; send_email?: boolean }>({
    query: ({ id, send_email = true }) => ({
      url: `/users/${id}/reset-credentials`,
      method: "POST",
      body: JSON.stringify({ send_email }),
    }),
    invalidatesTags: ["userList"],
  }),
  sendUserLoginInfo: builder.mutation<IApiResponse, { id: number; to_email?: string; subject?: string; message?: string }>({
    query: ({ id, ...body }) => ({
      url: `/users/${id}/send-login-info`,
      method: "POST",
      body: JSON.stringify(body),
    }),
  }),
  getUserLoginInfoPreview: builder.query<IApiResponse, number>({
    query: (id) => ({ url: `/users/${id}/login-info-preview`, method: "GET" }),
    keepUnusedDataFor: 0,
  }),
  getUserProfile: builder.query<IApiResponse, number>({
    query: (id) => ({ url: `/users/${id}/profile`, method: "GET" }),
    keepUnusedDataFor: 0,
  }),

  // ── Roles ────────────────────────────────────────────────────────────────────
  getRoles: builder.query<IApiResponse, void>({
    query: () => ({ url: "/roles", method: "GET" }),
    keepUnusedDataFor: 60,
    providesTags: ["role"],
  }),
  createRole: builder.mutation<IApiResponse, ICreateRolePayload>({
    query: (body) => ({ url: "/roles", method: "POST", body: JSON.stringify(body) }),
    invalidatesTags: ["role"],
  }),
  updateRole: builder.mutation<IApiResponse, { id: number } & ICreateRolePayload>({
    query: ({ id, ...body }) => ({ url: `/roles/${id}`, method: "PUT", body: JSON.stringify(body) }),
    invalidatesTags: ["role"],
  }),
  deleteRole: builder.mutation<IApiResponse, number>({
    query: (id) => ({ url: `/roles/${id}`, method: "DELETE" }),
    invalidatesTags: ["role"],
  }),

  // ── Privileges ───────────────────────────────────────────────────────────────
  getPrivileges: builder.query<IApiResponse, void>({
    query: () => ({ url: "/privileges", method: "GET" }),
    keepUnusedDataFor: 3600,
    providesTags: ["privilege"],
  }),

  // ── Online Payments (Easy Pay portal transactions) ───────────────────────────
  // Read-only. These rows are the payment gateway's audit trail — the portal
  // writes them, the CRM only reads. There is deliberately no create/update
  // /delete endpoint: corrections happen as voids and refunds, never as edits.
  getOnlinePayments: builder.query<IOnlinePaymentListResponse, IOnlinePaymentListPayload>({
    query: (params) => ({
      url: "/online-payments",
      method: "GET",
      // Blank filters are dropped rather than sent as empty strings, so the
      // backend sees "no filter" instead of "match the empty string".
      params: Object.fromEntries(
        Object.entries(params ?? {}).filter(([, v]) => v !== undefined && v !== null && v !== "")
      ),
    }),
    providesTags: ["onlinePayment"],
    keepUnusedDataFor: 0,
  }),
  getOnlinePayment: builder.query<IApiResponse, string>({
    query: (id) => ({ url: `/online-payments/${id}`, method: "GET" }),
    keepUnusedDataFor: 0,
  }),
  // ── Quick Payment Links ────────────────────────────────────────────────────
  getQuickPaymentLinks: builder.query<IApiResponse, { search?: string; status?: string; page?: number; per_page?: number }>({
    query: (params) => ({
      url: "/quick-payment-links",
      method: "GET",
      params: Object.fromEntries(
        Object.entries(params ?? {}).filter(([, v]) => v !== undefined && v !== null && v !== "")
      ),
    }),
    providesTags: ["quickPaymentLink"],
    keepUnusedDataFor: 0,
  }),
  // Prefills the form from an existing customer. Returns at most one match.
  lookupQuickPaymentCustomer: builder.query<IApiResponse, string>({
    query: (phone) => ({ url: "/quick-payment-links/lookup", method: "GET", params: { phone } }),
    keepUnusedDataFor: 0,
  }),
  createQuickPaymentLink: builder.mutation<IApiResponse, Record<string, any>>({
    query: (body) => ({ url: "/quick-payment-links", method: "POST", body }),
    invalidatesTags: ["quickPaymentLink"],
  }),
  // Separate from creation: staff choose recipients only after the link exists,
  // so a mail failure cannot read as "the link was not created".
  sendQuickPaymentLink: builder.mutation<IApiResponse, { id: number; customer: boolean; admin: boolean }>({
    query: ({ id, ...body }) => ({ url: `/quick-payment-links/${id}/send`, method: "POST", body }),
  }),
  cancelQuickPaymentLink: builder.mutation<IApiResponse, { id: number }>({
    query: ({ id }) => ({ url: `/quick-payment-links/${id}/cancel`, method: "POST" }),
    invalidatesTags: ["quickPaymentLink"],
  }),
  // Moves money. The portal decides void vs refund from Bambora's cut-off, so
  // there is deliberately no "kind" parameter for staff to get wrong.
  refundOnlinePayment: builder.mutation<IApiResponse, { id: string }>({
    query: ({ id }) => ({ url: `/online-payments/${id}/refund`, method: "POST" }),
    invalidatesTags: ["onlinePayment"],
  }),
  // Re-sends an already-sent notification. Not an edit to the audit trail and
  // never reaches the gateway, so the read-only rule above still holds.
  resendOnlinePaymentEmail: builder.mutation<IApiResponse, { id: string; recipient: "customer" | "admin" }>({
    query: ({ id, recipient }) => ({
      url: `/online-payments/${id}/resend-email`,
      method: "POST",
      body: { recipient },
    }),
  }),
}),


});

export const {
  useGetUserQuery,
  useLoginMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useCreateCategoryMutation,
  useCreateCompanyMutation,
  useCreateDepartmentMutation,
  useCreateCoursingMutation,
  useCreateTaxMutation,
  useCreateUrlMutation,
  useGetDepartmentQuery,
  useCreateProductBrandMutation,
  useCreateProductTagMutation,
  useCreateProductCategoryMutation,
  useCreateProductMutation,
  useCreatePrinterMutation,
  useGetCategoryQuery,
  useGetAllMainCategoryQuery,
  useDeleteCoursingMutation,
  useDeleteDepartmentMutation,
  useDeletePrinterMutation,
  useDeleteEmailTemplateMutation,
  useCreateEmailInvoiceMutation,
  useDeleteNoteTemplateMutation,
  useDeleteProductBrandMutation,
  useDeleteProductTagMutation,
  useDeleteTaxMutation,
  useDeleteUrlMutation,
  useDeleteProductCategoryMutation,
  useDeleteCategoryMutation,
  useGetProductQuery,
  useCreateGetAllProductMutation,
  useDeleteProductMutation,
  useGetLastProductIdQuery,
  useCreateModifierMutation,
  useGetAllModifierQuery,
  useDeleteModifierMutation,
  useGetTaxQuery,
  useGetUrlQuery,
  useGetCoursingQuery,
  useGetPrinterQuery,
  useGetProductTagQuery,
  useGetProductBrandQuery,
  useGetProductCategoryQuery,
  useGetSubCategoryByCategoryIdQuery,
  useGetSubCategoryByArrayQuery,
  useGetProductSubCategoryQuery,
  useGetAllCategoryNameQuery,
  useGetCtegoryFilterQuery,
  useGetLoopingTableQuery,
  useCreateLoopingTableMutation,
  useGetAllLoopingTableQuery,
  useDeleteLoopingTableMutation,
  useUpdateLoopingTableMutation,
  useInsertOrUpdateDataLoopingTableMutation,
  useGetCustomerQuery,
  useDeleteCustomerMutation,
  useDeleteWorkOrderMutation,
  useCreateCustomerMutation,
  useUpdateCustomerMutation,
  useGetCustomerTransactionQuery,
  useDeleteCustomerTransactionMutation,
  useCreateCustomerTransactionMutation,
  useDeleteCompanyMutation,
  useGetCompanyQuery,
  useGetAllSearchCompanyQuery,
  useCreateWorkOrderMutation,
  useGetLastWorkOrderIdQuery,
  useGetWorkOrderQuery,
  useUpdateInvoiceStatusQuery,
  useGetAllInvoiceQuery,
  useGetAllWorkOrderHistoryQuery,
  useGetAllAssignDriverHistoryQuery,
  useGetAllInvoiceHistoryQuery,
  useGetAllWorkOrderHistoryCustomerQuery,
  useCreateDriverMutation,
  useGetDriverQuery,
  useDeleteDriverMutation,
  useGetPriceHistoryQuery,
  useGetRoutePriceSuggestionQuery,
  useCreateCustomerProjectMutation,
  useDeleteCustomerProjectMutation,
  useGetCustomerProjectQuery,
  useGetWorkOrderByCustomerProjectQuery,
  useUpdateWorkOrderByCustomerProjectMutation,
  useGetSelectorWorkOrderByCustomerProjectQuery,
  useRemovedCustomerProjectMMutation,
  useCreateWorkOrderWithProjectMutation,
  useCreateEmailMutation,
  useSendQuoteEmailMutation,
  useCreateTextMutation,
  useCreateNoteMutation,
  useCreateNoteEmailMutation,
  useCreateEmailTemplateMutation,
  useCreateNoteTemplateMutation,
  useGetEmailTemplateQuery,
  useGetEmailTemplateByNameQuery,
  useGetNoteTemplateQuery,
  useGetWorkOrderNotesQuery,
  useGetActivityHistoryQuery,
  useGetProjectActivityHistoryQuery,
  useEditInvoiceMutation,
  useConvertToInvoiceMutation,
  useGetApInvoicesMutation,
  useSendApEmailMutation,
  useGetApHistoryMutation,
  useGetAllApHistoryQuery,
  useGetApCustomersByDateMutation,
  useCreateContractInvoiceMutation,
  useGetContractInvoicesQuery,
  useGetNextContractInvoiceIdQuery,
  useLazyGetContractInvoiceSuggestByDateQuery,
  useDeleteContractInvoiceMutation,
  useUpdateContractInvoicePaymentMutation,
  useCreateContractMutation,
  useGetContractsQuery,
  useLazyGetContractsQuery,
  useLazyGetContractLookupQuery,
  useLazyGetContractByIdQuery,
  useDeleteContractMutation,
  useGetTripSmsLogsQuery,
  useResendTripSmsMutation,
  useGetSmsTemplatesQuery,
  useCreateSmsTemplateMutation,
  useDeleteSmsTemplateMutation,
  useToggleSmsTemplateMutation,
  useDuplicateSmsTemplateMutation,
  useCreateDriverPaymentMutation,
  useGetDriverPaymentsQuery,
  useMarkDriverPaymentPaidMutation,
  useGetLeadsQuery,
  useGetLeadQuery,
  useUpdateLeadStatusMutation,
  useAddLeadCommunicationMutation,
  useConvertLeadMutation,
  useCreateLeadMutation,
  useDeleteLeadMutation,
  useGetPayrollSummaryQuery,
  useGetPayrollIncomeQuery,
  useRecordPaymentMutation,
  useGetPayrollExpensesQuery,
  useStorePayrollExpenseMutation,
  useDeletePayrollExpenseMutation,
  useGetPayrollTransactionsQuery,
  useGetTripsByCustomerQuery,
  useGetBalanceSheetAdvancedQuery,
  useGetBalanceSheetSummaryQuery,
  useGetBalanceSheetIncomeQuery,
  useGetBalanceSheetExpensesQuery,
  useGetBalanceSheetTransactionsQuery,
  useGetTripProfitQuery,
  useGetTripProfitByIdQuery,
  useGetTripProfitMonthlyQuery,
  useGetTripLegsQuery,
  useSplitTripLegsMutation,
  useAssignLegDriverMutation,
  useGetAvailableDriversQuery,
  useSendTripDriverSmsMutation,
  useGetNotificationsQuery,
  useMarkNotificationReadMutation,
  useMarkAllNotificationsReadMutation,
  useGetUsersListQuery,
  useCreateUserAccountMutation,
  useUpdateUserAccountMutation,
  useDeleteUserAccountMutation,
  useGenerateUserPasswordMutation,
  useSendUserCredentialsMutation,
  useResetUserCredentialsMutation,
  useSendUserLoginInfoMutation,
  useLazyGetUserLoginInfoPreviewQuery,
  useLazyGetUserProfileQuery,
  useGetRolesQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useGetPrivilegesQuery,
  useGetOnlinePaymentsQuery,
  useGetOnlinePaymentQuery,
  useResendOnlinePaymentEmailMutation,
  useRefundOnlinePaymentMutation,
  useGetQuickPaymentLinksQuery,
  useLazyLookupQuickPaymentCustomerQuery,
  useCreateQuickPaymentLinkMutation,
  useSendQuickPaymentLinkMutation,
  useCancelQuickPaymentLinkMutation,
  useGetBusinessProfileQuery,
  useUpdateBusinessProfileMutation,
  useGetPublicBrandingQuery,
} = attoDeskApi;
